/**
 * SOLID Review
 * S — Strong. One concern: the frontend's composition root — the one assembled graph, the Framework's host
 *     resources, and the one call made outside the provider.
 * O — Good. A new host resource is one field; a new component is the assembly's.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Three exports.
 * D — Good. The composition root: it may name concretes (`BrowserPlatform` names most of them), and it is
 *     the only module in framework/ besides that one which does.
 */
import { getWsConnectionState, setConnectionBannerHandlers, type IConfigHandlerConnectionBanner } from '../../infrastructure/graphql/apolloClient';
import { useUIStore } from '../stores/uiStore';
import { liveKitClientAdapterFactory } from '../../infrastructure/livekit/liveKitClientAdapterFactory';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { IConfigDepFrameworkHost } from '../uml-components/ComponentFramework';
import { clientContext } from '../client/ClientRequests';
import { FrontendAssembly, type IEntityFrontendGraph } from './FrontendAssembly';
import { browserPlatform } from './BrowserPlatform';
import { timed } from './UserTiming';

/**
 * THE ONE ASSEMBLED FRONTEND GRAPH (frontend strict-ports plan, FE1d; all four components since FE6a,
 * 2026-10-04): constructed, connected through `FrontendConnectors`, checked and built ONCE, here, at module
 * load — never in a render. App.tsx hands its Framework component to `ComponentFrameworkProvider`.
 *
 * This module was `adapterRegistry.ts` until FE6a: beside the graph it built the legacy adapters the
 * Framework's adapter sockets carried (Apollo room, game and session adapters, the room lookup, the
 * websocket monitor) into an `IAdapterRegistry` the Framework connected itself from. Those sockets, the
 * registry and the self-connection are gone; what is left is the graph, the two host resources the
 * Framework's context carries, and MaintenanceBanner's call.
 *
 * Kept as a module of its own (not App.tsx) for the reason it was split out on 2026-08-19: the port and UML
 * generators read the REAL assembled graph — a diagram drawn from a fixture documents the fixture — and
 * importing App.tsx pulls in the React tree.
 *
 * Timed as the User Timing measure `ml3d:assemble` (FE1's stop rule): construct, connect, check, build.
 */
const ASSEMBLE_MEASURE = 'ml3d:assemble';

/**
 * Apollo's slow-request banner, wired to the UI store — moved from App.tsx in strict-ports FE7, since naming
 * the Apollo module is composition work. Infrastructure must not reach into a Framework store, so the store's
 * setter is handed to it here, once, at module load: before the graph below exists, and so before any GraphQL
 * request can be issued.
 */
const configHandlerConnectionBanner: IConfigHandlerConnectionBanner = {
  onSlowStart: () => useUIStore.getState().setConnectionBanner('Server is busy — hang on…'),
  onSlowEnd: () => useUIStore.getState().setConnectionBanner(null),
};
setConnectionBannerHandlers(configHandlerConnectionBanner);

export const frontendGraph: IEntityFrontendGraph = timed(performance, ASSEMBLE_MEASURE, () => FrontendAssembly.assemble(browserPlatform()));

/**
 * The Framework's host resources (rule 2.3): the LAZY LiveKit client factory — the SDK chunk is
 * downloaded only when a player opts into video — and the websocket's live state, for AppRoot's watchdog.
 */
export const frameworkHost: IConfigDepFrameworkHost = {
  liveKitClientFactory: liveKitClientAdapterFactory,
  wsConnectionMonitor: { getState: getWsConnectionState },
};

/**
 * MaintenanceBanner's one call, for its mount OUTSIDE <ComponentFrameworkProvider>: the banner that says
 * "the backend is unreachable" must not itself depend on the provider (the 2026-08-17 white screen). It
 * is the Application's `checkServerHealth`, through the assembled Framework's own socket — guarded by the
 * use cases' contract, like every call React makes.
 */
const CHECK_HEALTH: TClientUseCaseInput<'checkServerHealth'> = {};
export const checkServerHealth = (): ReturnType<IPortClientUseCases['checkServerHealth']> =>
  frontendGraph.framework.requiredPorts.clientUseCasesSocket.getInterface().checkServerHealth(clientContext(), CHECK_HEALTH);
